/**
 * Ce qui manque à la fiche d'un enfant, dit au bon endroit et au bon moment.
 *
 * ## Le défaut corrigé (retour d'Éclosion, 24/10/2026)
 *
 * Quand l'enregistrement refusait une fiche incomplète, le parent était ramené
 * à la première étape en cause sous un bandeau rouge, « Une étape est
 * incomplète », qui restait ensuite affiché sur toutes les étapes et ne
 * nommait aucun champ. Les messages orange, eux, dataient de l'ouverture de la
 * fiche (vérification) ou du dernier clic (création) : ils restaient sous un
 * champ déjà rempli. Et la première étape corrigée, il fallait repasser toutes
 * les autres pour retrouver le bouton d'enregistrement.
 *
 * ## La règle
 *
 * Un champ n'est signalé qu'une fois qu'on l'a dit au parent : à l'ouverture
 * d'une fiche à vérifier, au clic sur « Continuer », à un enregistrement
 * refusé. Son message suit ensuite la fiche telle qu'elle est : il disparaît
 * dès que le champ est en règle, et revient si le parent l'efface. Un champ
 * jamais signalé ne s'affiche pas en cours de frappe.
 */

import { ETAPES, erreursEtape, type ContexteFiche, type Erreurs, type Etape, type Fiche } from './fiche';

/** Les champs déjà signalés au parent, étape par étape. */
export type Signales = Partial<Record<Etape, readonly string[]>>;

const ETAPES_A_REMPLIR = ETAPES.map((e) => e.cle).filter((cle) => cle !== 'recapitulatif');

/** Les erreurs actuelles d'une étape, réduites aux champs déjà signalés. */
export function erreursSignalees(actuelles: Erreurs, signales: readonly string[] | undefined): Erreurs {
  const e: Erreurs = {};
  for (const champ of signales ?? []) {
    if (actuelles[champ]) e[champ] = actuelles[champ];
  }
  return e;
}

/** Les champs en défaut de chaque étape, sur la fiche telle qu'elle est : ce qu'un refus signale. */
export function champsEnDefaut(fiche: Fiche, contexte: ContexteFiche = {}): Signales {
  const s: Partial<Record<Etape, string[]>> = {};
  for (const cle of ETAPES_A_REMPLIR) {
    const champs = Object.keys(erreursEtape(cle, fiche, contexte));
    if (champs.length > 0) s[cle] = champs;
  }
  return s;
}

/** Réunit deux relevés de champs signalés, étape par étape, sans doublon. */
export function reunir(a: Signales, b: Signales): Signales {
  const s: Partial<Record<Etape, string[]>> = {};
  for (const cle of ETAPES_A_REMPLIR) {
    const champs = [...new Set([...(a[cle] ?? []), ...(b[cle] ?? [])])];
    if (champs.length > 0) s[cle] = champs;
  }
  return s;
}

/** Les messages à afficher maintenant : ce qui, parmi les champs signalés, manque encore. */
export function manquesSignales(
  fiche: Fiche,
  contexte: ContexteFiche,
  signales: Signales,
): Partial<Record<Etape, Erreurs>> {
  const m: Partial<Record<Etape, Erreurs>> = {};
  for (const cle of ETAPES_A_REMPLIR) {
    const e = erreursSignalees(erreursEtape(cle, fiche, contexte), signales[cle]);
    if (Object.keys(e).length > 0) m[cle] = e;
  }
  return m;
}

/** Le nombre de champs encore à compléter par étape, pour l'indicateur d'étapes. */
export function nombresParEtape(manques: Partial<Record<Etape, Erreurs>>): Partial<Record<Etape, number>> {
  const n: Partial<Record<Etape, number>> = {};
  for (const cle of ETAPES_A_REMPLIR) {
    const combien = Object.keys(manques[cle] ?? {}).length;
    if (combien > 0) n[cle] = combien;
  }
  return n;
}
